/**
 * PJ の差分（`/pjs/$pjNumber/diff`）— 案件が変えた要素ごとに、欄の変更前（公開中の仕様）と
 * 変更後を 3 列（欄 │ 変更前 │ 変更後）で並べる。
 *
 * **探す画面と見比べる画面を分ける**（案件モードの原則）。一覧と変更一覧は左右に割らず印だけを
 * 出し、欄の単位の見比べはこの 1 枚に集める。**画面から来る入口は無い** — 入口だった変更一覧の帯の
 * 「差分を見る」は #4807 で外した（Dhik の指示 2026-09-28）。URL を直に開くと来られる。
 *
 * **語の差分は出さない。** 欄の単位で、変わった欄の変更前を赤の地色、変更後を緑の地色で塗る
 * だけにする。片側に無い欄は、無い側を斜線の空札にする — 空欄のままだと「値が空文字」と
 * 「欄そのものが無い」が見分けられない。
 *
 * **どの欄が変わったかは API が決める**（`getPjDiff`。MCP の `get_spec_changes` と同じ 1 関数）。
 * 画面は返った `changed` を塗るだけで、比べ直さない。
 *
 * **要素ごとに名前 ・ 操作の札 ・ 変わった欄の「変更前 → 変更後」だけを見せる**（Dhik 2026-09-29「本当に出すべき
 * 情報を 1 or 2 個に」）。状態の札は見出しの `title`、「公開中の仕様」と案件番号は列見出しの `title`、
 * 「詳細で見る」は見出し行に指を載せたときだけ出す。見出しの集計・要素の型の札・「（無し）」・
 * 「変わった欄は無い」・目次の見出しと型の小見出しと色の点は置かない。操作の絞り込みと
 * 「変わっていない欄も出す」は「ほかの絞り込み」に畳み、業務ルールのキーの決め方は見出しを押して開く。
 * 実装 ・ 使う表 ・ 使う列 ・ 形の欄は出さない（内部の実装の話と形の札は出さない、09-28）。
 * **値は JSON のまま出さない。** 番号は `getPjDiff` の `labels` で要素の名前にし、業務ルールの中身は
 * 行に割って揃える（判定表の行・式の段・並べ替えのキーの行、その下にキーの決め方をキーごとの
 * 小見出しで。`pj-diff-rows.ts`）。小見出しには札を置かず、足した・変えたキーの名前を緑、外したキーを赤と打ち消しで塗る。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の差分を、型と操作で絞れる要素ごとの 3 列の表と、変わった要素の目次で描く。
 * @graph-connects groupPjDiffItems [calls] 要素を型ごとに束ねる
 * @graph-connects filterPjDiffItems [calls] 型と操作で絞る
 * @graph-connects SpecNodeLinkInPj [calls] 要素の詳細への導線
 */
import type { GetPjDiffOutput } from '@cortex/domain-spec-console';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSpecLocale } from '../i18n/spec-locale.js';

import { EmptyState } from '../components/spec/fallback-states.js';
import { SPEC_DIFF_WORD_CLASS } from '../components/spec/SpecDiffState.js';
import { SpecNodeLinkInPj } from '../components/spec/SpecNodeLink.js';
import { displayLabel } from '../components/spec/UntranslatedBadge.js';
import { formatPjLabel } from '../lib/pj-label.js';
import { nodeTypeLabelKey } from '../lib/spec-node-types.js';
import {
  formatPjDiffFieldValue,
  pjDiffFieldTitle,
  pjDiffRowsOf,
  type PjDiffLabels,
} from './pj-diff-rows.js';
import {
  DEFAULT_PJ_DIFF_FILTER,
  filterPjDiffItems,
  groupPjDiffItems,
  isPjDiffNodeType,
  PJ_DIFF_CHANGE_KINDS,
  pjDiffAnchorId,
  pjDiffCountByKind,
  pjDiffCountByType,
  pjDiffDetailPj,
  pjDiffTypesOf,
  type PjDiffChangeKind,
  type PjDiffFilter,
  type PjDiffViewItem,
} from './pj-diff-view.js';
import { PJ_DIFF_ROUTE } from './route-paths.js';

/** 文言を引く関数。 @graph-connects none */
type Translate = (key: string, params?: Record<string, unknown>) => string;

/**
 * 操作の札の見た目。追加は緑のベタ、変更は緑の枠、削除は赤のベタ。
 * @graph-connects none
 */
const CHANGE_KIND_BADGE: Readonly<Record<PjDiffChangeKind, string>> = {
  add: 'border border-[var(--color-diff-added-pane)] bg-[var(--color-diff-added-pane)] text-[var(--color-bg-base)]',
  update: 'border border-[var(--color-diff-added-pane)] text-[var(--color-diff-added-pane)]',
  delete:
    'border border-[var(--color-diff-removed-pane)] bg-[var(--color-diff-removed-pane)] text-[var(--color-bg-base)]',
};

/**
 * 3 列の表の中のキーの小見出しに付ける差分の色。**表の中では札を出さず、名前を語の強調と同じ
 * 濃さで塗る**（Dhik 2026-09-29「セル内の変わったところの色を濃くするで統一してほしい。他に左右
 * viewでタグでdiffを表示しているところがあれば同じ対応して」）。小見出しは 3 列にまたがるので側を
 * 持たないが、足した・変えたキーは入った側の緑、外したキーは消えた側の赤と打ち消しで言える。
 * 状態の語は名前を指すと出す（`title`）。要素の見出しの操作の札は表の外なので残す。
 * @graph-connects SPEC_DIFF_WORD_CLASS [references] 側ごとの語の地色
 */
const DERIVE_KEY_MARK_CLASS: Readonly<Record<PjDiffChangeKind, string>> = {
  add: SPEC_DIFF_WORD_CLASS.after,
  update: SPEC_DIFF_WORD_CLASS.after,
  delete: `${SPEC_DIFF_WORD_CLASS.before} line-through`,
};

/** 片側に無い欄の空札。斜線の地は罫線の色で描く。 @graph-connects none */
const ABSENT_CELL =
  'bg-[repeating-linear-gradient(135deg,var(--color-border)_0,var(--color-border)_1px,transparent_1px,transparent_6px)] text-[var(--color-text-muted)]';

/**
 * 型の表示名。語彙に無い型は綴りのまま出す（「その他」に丸めない）。
 *
 * 訳語は型バッジと同じ `nodeTypeLabelKey` から引く。ここで自前の対応表を持つと、名前を
 * 変えたときに片方だけが直り、同じ型が画面によって違う呼び名で出る（業務ルールがバッジだけ
 * 「処理」のまま残った）。
 *
 * @param nodeType 要素の型
 * @param t 文言
 * @returns 型の表示名
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 要素の型の綴りから表示名を引く（語彙に無い型は綴りのまま）。
 * @graph-connects nodeTypeLabelKey [calls] 型の表示名の翻訳キー
 */
export function pjTypeLabel(nodeType: string, t: Translate): string {
  return isPjDiffNodeType(nodeType) ? t(nodeTypeLabelKey(nodeType)) : nodeType;
}

/**
 * `PjDiffPage` の props。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business PJ の差分の入力（案件番号と差分の読取の応答）
 * @graph-connects none
 */
export interface PjDiffPageProps {
  /** いま見ている案件の番号 */
  pjNumber: number;
  /** 差分の読取（`getPjDiff`）の応答 */
  diff: GetPjDiffOutput;
}

/**
 * PJ の差分のページ。
 *
 * 絞り込み・選択は画面の中の状態に持ち、URL には出さない（見比べの途中の位置であって、
 * 他人へ渡す状態ではない）。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の差分の見出し・絞り込みの帯・型ごとの要素の表・目次を描く。
 * @graph-connects groupPjDiffItems [calls] 要素を型ごとに束ねる
 * @graph-connects filterPjDiffItems [calls] 型と操作で絞る
 */
export function PjDiffPage({ pjNumber, diff }: PjDiffPageProps) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<PjDiffFilter>(DEFAULT_PJ_DIFF_FILTER);
  const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);

  const shown = filterPjDiffItems(diff.items, filter);
  const groups = groupPjDiffItems(shown);
  const labels: PjDiffLabels = new Map((diff.labels ?? []).map((entry) => [entry.id, entry.label]));

  const jumpTo = (nodeId: number) => {
    setSelectedNodeId(nodeId);
    document.getElementById(pjDiffAnchorId(nodeId))?.scrollIntoView?.({ block: 'start' });
  };

  return (
    <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
      <header className="flex flex-wrap items-center gap-3">
        <h1
          data-pj-state={diff.pjState}
          title={diff.pjState === 'open' ? t('pjs.stateOpen') : t('pjs.stateMerged')}
          className="text-2xl font-bold text-[var(--color-text-primary)]"
        >
          {t('pjFieldDiff.title', { pj: formatPjLabel(pjNumber) })}
        </h1>
        <Link
          to={PJ_DIFF_ROUTE}
          params={{ pjNumber: String(pjNumber) }}
          className="text-sm text-[var(--color-accent-primary)] underline"
        >
          {t('pjFieldDiff.backToChanges')}
        </Link>
      </header>

      {diff.items.length === 0 ? (
        <EmptyState message={t('pjFieldDiff.empty')} />
      ) : (
        <>
          <PjDiffFilterBand items={diff.items} filter={filter} onChange={setFilter} />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_16rem]">
            <div className="flex min-w-0 flex-col gap-6">
              {groups.length === 0 ? (
                <EmptyState message={t('pjFieldDiff.emptyFiltered')} />
              ) : (
                groups.map((group) => (
                  <section key={group.nodeType} data-pj-diff-group={group.nodeType}>
                    <h2 className="mb-2 text-sm font-semibold text-[var(--color-text-primary)]">
                      {t('pjFieldDiff.groupTitle', {
                        type: pjTypeLabel(group.nodeType, t),
                        count: group.items.length,
                      })}
                    </h2>
                    <div className="flex flex-col gap-3">
                      {group.items.map((item) => (
                        <PjDiffEntry
                          key={item.nodeId}
                          item={item}
                          labels={labels}
                          pjNumber={pjNumber}
                          showUnchanged={filter.showUnchanged}
                          selected={item.nodeId === selectedNodeId}
                        />
                      ))}
                    </div>
                  </section>
                ))
              )}
            </div>
            <PjDiffToc groups={groups} selectedNodeId={selectedNodeId} onJump={jumpTo} />
          </div>
        </>
      )}
    </div>
  );
}

/** `PjDiffFilterBand` の props。 @graph-connects none */
interface PjDiffFilterBandProps {
  items: readonly PjDiffViewItem[];
  filter: PjDiffFilter;
  onChange: (filter: PjDiffFilter) => void;
}

/**
 * 切替 1 つの見た目。選んでいるものだけ塗る。
 *
 * @param active 選んでいるか
 * @returns class
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 絞り込みの切替 1 つの見た目を、選んでいるかで決める。
 * @graph-connects none
 */
function toggleClass(active: boolean): string {
  return active
    ? 'rounded-sm border border-[var(--color-accent-primary)] bg-[var(--color-accent-primary)] px-2 py-0.5 text-xs text-[var(--color-bg-base)]'
    : 'rounded-sm border border-[var(--color-border)] px-2 py-0.5 text-xs text-[var(--color-text-secondary)]';
}

/**
 * 絞り込みの帯 — 型・操作の切替（各ボタンに件数）と「変わっていない欄も出す」。
 *
 * **件数は絞る前の全要素で数える。** 絞った後の件数を出すと、選んでいない切替の数字が
 * 選ぶたびに変わり、どれを押せば何件になるかが読めない。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 差分の型・操作の切替と、変わっていない欄を出すかの切替を描く。
 * @graph-connects pjDiffCountByType [calls] 型ごとの件数
 * @graph-connects pjDiffCountByKind [calls] 操作ごとの件数
 */
function PjDiffFilterBand({ items, filter, onChange }: PjDiffFilterBandProps) {
  const { t } = useTranslation();
  const typeCounts = pjDiffCountByType(items);
  const kindCounts = pjDiffCountByKind(items);

  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-md border border-[var(--color-border)] px-3 py-2">
      <div
        className="flex flex-wrap items-center gap-1.5"
        role="group"
        aria-label={t('pjFieldDiff.filterType')}
      >
        <span className="text-xs text-[var(--color-text-muted)]">
          {t('pjFieldDiff.filterType')}
        </span>
        <button
          type="button"
          aria-pressed={filter.nodeType === ''}
          className={toggleClass(filter.nodeType === '')}
          onClick={() => {
            onChange({ ...filter, nodeType: '' });
          }}
        >
          {`${t('pjFieldDiff.filterAll')} ${String(items.length)}`}
        </button>
        {pjDiffTypesOf(items).map((nodeType) => (
          <button
            key={nodeType}
            type="button"
            aria-pressed={filter.nodeType === nodeType}
            className={toggleClass(filter.nodeType === nodeType)}
            onClick={() => {
              onChange({ ...filter, nodeType });
            }}
          >
            {`${pjTypeLabel(nodeType, t)} ${String(typeCounts.get(nodeType) ?? 0)}`}
          </button>
        ))}
      </div>
      <details data-pj-diff-more-filters className="ml-auto">
        <summary className="cursor-pointer text-xs text-[var(--color-text-muted)]">
          {t('pjFieldDiff.moreFilters')}
        </summary>
        <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2">
          <div
            className="flex flex-wrap items-center gap-1.5"
            role="group"
            aria-label={t('pjFieldDiff.filterOperation')}
          >
            <span className="text-xs text-[var(--color-text-muted)]">
              {t('pjFieldDiff.filterOperation')}
            </span>
            <button
              type="button"
              aria-pressed={filter.changeKind === ''}
              className={toggleClass(filter.changeKind === '')}
              onClick={() => {
                onChange({ ...filter, changeKind: '' });
              }}
            >
              {`${t('pjFieldDiff.filterAll')} ${String(items.length)}`}
            </button>
            {PJ_DIFF_CHANGE_KINDS.map((changeKind) => (
              <button
                key={changeKind}
                type="button"
                aria-pressed={filter.changeKind === changeKind}
                className={toggleClass(filter.changeKind === changeKind)}
                onClick={() => {
                  onChange({ ...filter, changeKind });
                }}
              >
                {`${t(`pjFieldDiff.operation.${changeKind}`)} ${String(kindCounts[changeKind])}`}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
            <input
              type="checkbox"
              checked={filter.showUnchanged}
              onChange={(event) => {
                onChange({ ...filter, showUnchanged: event.target.checked });
              }}
              className="h-4 w-4 accent-[var(--color-accent-primary)]"
            />
            {t('pjFieldDiff.showUnchanged')}
          </label>
        </div>
      </details>
    </div>
  );
}

/** `PjDiffEntry` の props。 @graph-connects none */
interface PjDiffEntryProps {
  item: PjDiffViewItem;
  /** 番号 → 要素の名前（`getPjDiff` の `labels`） */
  labels: PjDiffLabels;
  pjNumber: number;
  showUnchanged: boolean;
  selected: boolean;
}

/**
 * 要素 1 件 — 見出し行（名前・操作・指を載せたときだけ出す詳細への導線）と、欄 │ 変更前 │ 変更後 の表。
 * 業務ルールのキーの決め方は、表の中のキーの決め方の見出しを押すと、キーごとの小見出し（追加 ／ 削除 ／
 * 変更の印）と欄を開く。既定は閉じる。変わった欄が無い要素は見出し行だけにする。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 差分の要素 1 件を、見出し行と、欄ごと（業務ルールは行・キーごと）の変更前・変更後の表で描く。
 * @graph-connects pjDiffRowsOf [calls] 画面の行の組み立て
 * @graph-connects pjDiffFieldTitle [calls] 欄の見出し
 * @graph-connects formatPjDiffFieldValue [calls] 欄の値の文字化
 * @graph-connects useSpecLocale [calls] 表示中の言語の解決
 * @graph-connects displayLabel [calls] 要素の名前の穴埋め
 * @graph-connects pjTypeLabel [calls] 型の表示名
 * @graph-connects pjDiffAnchorId [calls] 目次から移る錨
 * @graph-connects isPjDiffNodeType [calls] 詳細の行き先を引ける型か
 * @graph-connects pjDiffDetailPj [calls] 詳細に重ねる案件
 * @graph-connects formatPjLabel [calls] 変更後の見出しの案件番号
 * @graph-connects PjDiffCell [calls] 表の 1 セル
 * @graph-connects SpecNodeLinkInPj [calls] 要素の詳細への導線
 */
function PjDiffEntry({ item, labels, pjNumber, showUnchanged, selected }: PjDiffEntryProps) {
  const { t } = useTranslation();
  const locale = useSpecLocale();
  const rows = pjDiffRowsOf(item, showUnchanged);
  const [deriveOpen, setDeriveOpen] = useState(false);
  /* キーの決め方は表の最後にまとまっている。見出しの行より後ろは、開いたときだけ描く。 */
  const deriveAt = rows.findIndex((row) => row.kind === 'deriveTitle');
  const label = displayLabel(item.label, t, false);
  const context = { labels, t, locale: locale ?? 'ja', nodeType: item.nodeType };

  return (
    <article
      id={pjDiffAnchorId(item.nodeId)}
      data-pj-diff-node={item.nodeId}
      data-selected={selected}
      className={`scroll-mt-4 rounded-md border ${
        selected ? 'border-[var(--color-accent-primary)]' : 'border-[var(--color-border)]'
      }`}
    >
      <div
        className={`group flex flex-wrap items-center gap-2 px-3 py-2 ${rows.length === 0 ? '' : 'border-b border-[var(--color-border)]'}`}
      >
        <span className="font-semibold text-[var(--color-text-primary)]">{label}</span>
        <span
          data-change-kind={item.changeKind}
          className={`rounded-sm px-1.5 py-0.5 text-xs ${CHANGE_KIND_BADGE[item.changeKind]}`}
        >
          {t(`pjFieldDiff.operation.${item.changeKind}`)}
        </span>
        {isPjDiffNodeType(item.nodeType) && (
          <span
            data-pj-diff-open-detail
            className="ml-auto text-sm opacity-0 group-hover:opacity-100 focus-within:opacity-100"
          >
            <SpecNodeLinkInPj
              nodeType={item.nodeType}
              nodeId={String(item.nodeId)}
              pjNumber={pjDiffDetailPj(item, pjNumber)}
              className="text-[var(--color-accent-primary)] underline"
            >
              {t('pjFieldDiff.openDetail')}
            </SpecNodeLinkInPj>
          </span>
        )}
      </div>
      {rows.length > 0 && (
        <table className="w-full table-fixed border-collapse text-sm">
          <thead>
            <tr className="text-left text-xs">
              <th className="w-40 px-3 py-1.5 font-medium text-[var(--color-text-muted)]">
                {t('pjFieldDiff.colField')}
              </th>
              <th
                title={t('pjFieldDiff.colBeforeNote')}
                className="bg-[var(--color-diff-removed-cell)] px-3 py-1.5 font-medium text-[var(--color-text-primary)]"
              >
                {t('pjFieldDiff.colBefore')}
              </th>
              <th
                title={formatPjLabel(pjNumber)}
                className="bg-[var(--color-diff-added-cell)] px-3 py-1.5 font-medium text-[var(--color-text-primary)]"
              >
                {t('pjFieldDiff.colAfter')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              if (row.kind === 'deriveTitle') {
                return (
                  <tr key="derive-title" data-pj-diff-heading="derive">
                    <td
                      colSpan={3}
                      className="border-t border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-1.5 text-xs font-bold text-[var(--color-text-secondary)]"
                    >
                      <button
                        type="button"
                        aria-expanded={deriveOpen}
                        className="cursor-pointer"
                        onClick={() => {
                          setDeriveOpen((open) => !open);
                        }}
                      >
                        {`${deriveOpen ? '▾' : '▸'} ${t('pjFieldDiff.deriveTitle')}`}
                      </button>
                    </td>
                  </tr>
                );
              }
              if (!deriveOpen && deriveAt >= 0 && index > deriveAt) return null;
              if (row.kind === 'deriveKey') {
                const keyName = labels.get(row.key) ?? String(row.key);
                return (
                  <tr
                    key={`derive-${String(row.key)}`}
                    data-pj-diff-derive-key={row.key}
                    data-derive-mark={row.mark ?? 'none'}
                  >
                    <td
                      colSpan={3}
                      className="border-t border-[var(--color-border)] px-3 pt-2 pb-1 text-sm font-bold text-[var(--color-text-primary)]"
                    >
                      {row.mark === null ? (
                        keyName
                      ) : (
                        <span
                          title={t(`pjFieldDiff.operation.${row.mark}`)}
                          className={DERIVE_KEY_MARK_CLASS[row.mark]}
                        >
                          {keyName}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              }
              const { field, path } = row;
              return (
                <tr
                  key={field.field}
                  data-pj-diff-field={field.field}
                  data-changed={field.changed}
                  className="border-t border-[var(--color-border)] align-top"
                >
                  <td className="px-3 py-1.5 text-xs text-[var(--color-text-muted)]">
                    {pjDiffFieldTitle(path, labels, t)}
                  </td>
                  <PjDiffCell
                    text={formatPjDiffFieldValue(path, field.before, context)}
                    changed={field.changed}
                    side="before"
                    struck={item.changeKind === 'delete'}
                  />
                  <PjDiffCell
                    text={formatPjDiffFieldValue(path, field.after, context)}
                    changed={field.changed}
                    side="after"
                    struck={false}
                  />
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </article>
  );
}

/** `PjDiffCell` の props。 @graph-connects none */
interface PjDiffCellProps {
  /** 表示する文字。その側に値が無ければ null */
  text: string | null;
  changed: boolean;
  side: 'before' | 'after';
  /** 取り消し線を引くか（削除の要素の変更前） */
  struck: boolean;
}

/**
 * 表の 1 セル。値が無ければ斜線の空札、変わった欄は側ごとの地色、変わっていない欄は薄い字。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 差分の欄の片側 1 セルを、無い・変わった・変わっていないの 3 通りで描く。
 * @graph-connects none
 */
function PjDiffCell({ text, changed, side, struck }: PjDiffCellProps) {
  if (text === null) {
    return <td data-cell={`${side}-absent`} className={`px-3 py-1.5 text-xs ${ABSENT_CELL}`} />;
  }

  const tone = changed
    ? side === 'before'
      ? 'bg-[var(--color-diff-removed-cell)] text-[var(--color-text-primary)]'
      : 'bg-[var(--color-diff-added-cell)] text-[var(--color-text-primary)]'
    : 'text-[var(--color-text-muted)]';

  return (
    <td
      data-cell={`${side}-${changed ? 'changed' : 'same'}`}
      className={`px-3 py-1.5 break-words whitespace-pre-wrap ${tone} ${struck ? 'line-through' : ''}`}
    >
      {text}
    </td>
  );
}

/** `PjDiffToc` の props。 @graph-connects none */
interface PjDiffTocProps {
  groups: ReturnType<typeof groupPjDiffItems>;
  selectedNodeId: number | null;
  onJump: (nodeId: number) => void;
}

/**
 * 右の目次 — 要素名だけを本文と同じ並びで並べる（型の小見出し・色の点・見出しは本文の群の見出しと
 * 操作の札で読めるので置かない）。押すとその要素へ移り選択表示する。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 変わった要素の目次を名前だけで本文と同じ並びに描き、押された要素へ移す。
 * @graph-connects none
 */
function PjDiffToc({ groups, selectedNodeId, onJump }: PjDiffTocProps) {
  const { t } = useTranslation();

  return (
    <nav
      aria-label={t('pjFieldDiff.toc')}
      className="self-start rounded-md border border-[var(--color-border)] px-3 py-2 lg:sticky lg:top-0"
    >
      <ul className="flex flex-col">
        {groups.flatMap((group) =>
          group.items.map((item) => (
            <li key={item.nodeId}>
              <button
                type="button"
                data-toc-node={item.nodeId}
                aria-current={item.nodeId === selectedNodeId ? 'true' : undefined}
                className={`flex w-full items-center rounded-sm px-1 py-0.5 text-left text-xs ${
                  item.nodeId === selectedNodeId
                    ? 'bg-[var(--color-bg-active)] text-[var(--color-text-primary)]'
                    : 'text-[var(--color-text-secondary)]'
                }`}
                onClick={() => {
                  onJump(item.nodeId);
                }}
              >
                <span className="truncate">{displayLabel(item.label, t, false)}</span>
              </button>
            </li>
          )),
        )}
      </ul>
    </nav>
  );
}
